import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import React from 'react';
import dayjs from 'libs/dayjs';
import PageA4 from '../Elements/Pages/PageA4';
import Table from '../Elements/Table/Table';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import SignatureBlock from 'PDF/Signatures';
import { API_PATH } from 'config';

const styles = StyleSheet.create({
  page: {
    paddingBottom: '4cm',
    paddingTop: '4cm',
    paddingHorizontal: '1.5cm',
    backgroundColor: '#ffffff',
    minHeight: '100vh',
  },
  section: {
    marginBottom: '1.5cm',
    padding: '0.8cm',
    backgroundColor: '#ffffff',
    borderRadius: '0.3cm',
    border: '1px solid #e8e8e8',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  body: {
    flex: 1,
    paddingTop: '0.5cm',
  },
  titleContainer: {
    fontSize: '18px',
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    backgroundColor: '#548235',
    paddingVertical: '0.6cm',
    paddingHorizontal: '1cm',
    textAlign: 'center',
    borderRadius: '0.3cm',
    marginBottom: '1cm',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  greeting: {
    fontSize: '16px',
    fontFamily: 'Helvetica-Bold',
    color: '#2c3e50',
    marginBottom: '0.8cm',
    textTransform: 'capitalize',
  },
  paragraph: {
    fontSize: '11px',
    fontFamily: 'Helvetica',
    color: '#34495e',
    lineHeight: 1.6,
    textAlign: 'justify',
    marginBottom: '0.8cm',
  },
  header: {
    width: '100vw',
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    height: '3.5cm',
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #548235',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: '1.5cm',
    paddingVertical: '0.4cm',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  footer: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    right: '0px',
    width: '100vw',
    height: '3.5cm',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: '0.6cm',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
  },
});

const columns = [
  {
    width: '13%',
    title: 'Categoría',
    key: 'category',
  },
  {
    width: '17%',
    title: 'Ascensores',
    key: 'ascensores',
  },
  {
    width: '35%',
    title: 'Descripción',
    key: 'item_description',
  },
  {
    width: '35%',
    title: 'Observación',
    key: 'observation',
  },
];

function MaintanceDocument(props) {
  const { items, report, config } = props;
  const { name, address, commune } = report;
  const {
    general_observation: observation,
    specialist_name: specialistName,
    recepcionist_name: recepcionistName,
    specialist_cargo: specialistCargo,
    specialist_especialidad: specialistEspecialidad,
    specialist_otros: specialistOtros,
    insert_in: insertIn,
  } = report;
  const {
    recepcionist_signature: recepcionistSignature,
    recepcionist_charge: recepcionistCharge,
    specialist_signature: specialistSignature,
    id_maintance_report: idReport,
  } = report;

  return (
    <Document>
      <PageA4 style={styles.page}>
        {/* Header fijo */}
        <View style={styles.header} fixed>
          <Header title={`Informe de Mantención N°${idReport}`} config={config} date={insertIn} />
        </View>

        {/* Contenido principal */}
        <View style={styles.body}>
          {/* Sección 1: Saludo y Introducción */}
          <View style={styles.section}>
            <Text style={styles.greeting}>Estimados {name},</Text>
            <Text style={styles.paragraph}>
              Es un placer presentarle el informe detallado de mantenimiento realizado en {address},
              en la comuna de {commune} realizado el{' '}
              {dayjs(insertIn).format('DD [de] MMMM [del] YYYY')}. Los especialistas han llevado a
              cabo una revisión exhaustiva de las instalaciones, a fin de garantizar su
              funcionamiento óptimo y seguridad.
            </Text>
            <Text style={styles.paragraph}>
              Este informe incluye un registro completo de los trabajos realizados y las
              observaciones encontradas durante la inspección. Cualquier duda o pregunta sobre los
              resultados de este mantenimiento, por favor no dude en contactarnos a través de
              nuestro correo electrónico.
            </Text>
          </View>

          {/* Sección 2: Informe de Items */}
          <View style={styles.section}>
            <View style={styles.titleContainer}>
              <Text>Informe de Items</Text>
            </View>
            <Text style={styles.paragraph}>
              En la siguiente tabla, se observan los items revisados y los ascensores involucrados.
            </Text>
            <Table columns={columns} data={items} />
          </View>

          {/* Sección 3: Observaciones Generales */}
          <View style={styles.section}>
            <View style={styles.titleContainer}>
              <Text>Observaciones Generales</Text>
            </View>
            <Text style={styles.paragraph}>{observation}</Text>
          </View>

          {/* Sección 4: Información de Entrega */}
          <View style={[styles.section]} wrap={false}>
            <View style={styles.titleContainer}>
              <Text>Información de Entrega</Text>
            </View>
            <Text style={styles.paragraph}>
              A continuación, se encuentra la firma, la información del especialista y de quien
              recibe la atención. Esto, cuenta como una constancia firmada de que se realizó la
              atención.
            </Text>
            <SignatureBlock
              specialistSignature={specialistSignature}
              recepcionistName={recepcionistName}
              recepcionistSignature={recepcionistSignature}
              recepcionistCharge={recepcionistCharge}
              specialistName={specialistName}
              specialistEspecialidad={specialistEspecialidad}
              specialistCargo={specialistCargo}
              specialistOtros={specialistOtros}
            />
          </View>
        </View>

        {/* Footer fijo */}
        <View style={styles.footer} fixed>
          <Footer config={config} />
        </View>
      </PageA4>
    </Document>
  );
}

export default MaintanceDocument;
